/* eslint-disable no-unused-expressions */
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { useLocation } from "react-router-dom";
import { Dialog, DialogTitle } from "@mui/material";
import React, { useRef, useState, useEffect } from "react";
import { Box, Button, TextField, Fade } from "@mui/material";
import { DialogActions, Typography, Skeleton } from "@mui/material";

// component
import FormService from "Lib/API/Forms";
import { Fonts, FontWeights } from "theme/fontConfig";

const OtpSchema = Yup.object().shape({
  otp: Yup.array()
    .of(
      Yup.string()
        .matches(/^[0-9]$/, "Must be a digit")
        .required("Required")
    )
    .min(6, "Enter 6 digits"),
});

const OtpModal = ({
  open,
  handleClose,
  setVerifyModal,
  onHandleSubmit,
  tempToken,
}) => {
  const inputRefs = useRef([]);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const contactId = query.get("Id");
  // state
  const [coolDown, setCoolDown] = useState(0);
  const [otpToken, setOtpToken] = useState(null);
  const [email, setEmail] = useState(null);
  const [loading, setIsLoading] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleChange = (e, index, setFieldValue) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setFieldValue(`otp[${index}]`, value);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index, values, setFieldValue) => {
    if (e.key === "Backspace") {
      if (values.otp[index]) {
        setFieldValue(`otp[${index}]`, "");
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        setFieldValue(`otp[${index - 1}]`, "");
      }
    }
  };

  const requestOtp = async () => {
    const body = {
      contactId: contactId,
      type: "payment",
    };
    setIsLoading(true);
    try {
      const res = await FormService.sentCode(tempToken?.token, body);
      setOtpToken(res?.result?.token);
      setEmail(res?.result?.email);
    } catch (error) {
      setErrorMessage(error?.data?.message);
      setTimeout(() => {
        setErrorMessage(null);
      }, 7000);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (coolDown > 0) {
      const timer = setInterval(() => {
        setCoolDown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [coolDown]);

  // Send OTP when modal opens
  useEffect(() => {
    if (open && tempToken?.token) {
      requestOtp();
      setResendCount(0);
      setCoolDown(30); // first resend after 30s
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tempToken]);

  const handleResend = () => {
    requestOtp();
    setResendCount((prev) => prev + 1);
    if (resendCount === 0) {
      setCoolDown(60); // after first, wait 1min
    } else {
      setCoolDown(60);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      className="otp-dialog"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          boxShadow: 10,
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h6"
          fontWeight="bold"
          align="center"
          sx={{ fontSize: Fonts.GeneralHeaderFont.fontSize }}
        >
          Verify
        </Typography>
      </DialogTitle>

      <Box
        sx={{
          p: {
            xs: 0,
            sm: 0,
            md: 2,
          },
        }}
      >
        <Typography variant="body2" align="center" sx={{ mb: 2, fontSize: Fonts.GeneralLargeBodyFont.fontSize }}>
          To update your payment methods, please enter the verification code
          that was sent to your email <br />
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Skeleton
                variant="text"
                sx={{ fontSize: Fonts.GeneralSmallBodyFont.fontSize, width: 200, height: 50 }}
              />
            </Box>
          ) : (
            <Box sx={{ fontWeight: FontWeights.SemiBold }}>{email}</Box>
          )}
        </Typography>

        <Formik
          initialValues={{ otp: ["", "", "", "", "", ""] }}
          validationSchema={OtpSchema}
          onSubmit={async (values) => {
            const otpValue = values.otp.join("");

            const body = {
              code: otpValue,
              token: otpToken,
            };
            setIsLoading(true);
            try {
              const res = await FormService.verifyCode(tempToken?.token, body);
              localStorage.setItem("accountID", res?.result?.accountInfo?._id);
              localStorage.setItem(
                "userAuthData",
                JSON.stringify({
                  token: res?.result?.tokenForOneHour,
                })
              );
              setVerifyModal(false);
              onHandleSubmit(res?.result);
            } catch (error) {
              setErrorMessage(error?.data?.message);
              setTimeout(() => {
                setErrorMessage(null);
              }, 7000);
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <Box
                display="flex"
                justifyContent="center"
                sx={{
                  gap: {
                    xs: 0.5,
                    sm: 1,
                    md: 1.5,
                  },
                }}
                mb={2}
              >
                {values?.otp?.map((val, index) => (
                  <Field key={index} name={`otp[${index}]`}>
                    {() => (
                      <TextField
                        inputRef={(el) => (inputRefs.current[index] = el)}
                        value={val}
                        onChange={(e) => handleChange(e, index, setFieldValue)}
                        onKeyDown={(e) =>
                          handleKeyDown(e, index, values, setFieldValue)
                        }
                        onPaste={(e) => {
                          e.preventDefault();
                          const pasted = e.clipboardData
                            .getData("text")
                            .replace(/\D/g, ""); // digits only
                          if (!pasted) return;

                          const otpArray = [...values.otp];
                          for (
                            let i = 0;
                            i < pasted.length && index + i < 6;
                            i++
                          ) {
                            otpArray[index + i] = pasted[i];
                          }
                          otpArray.forEach((digit, i) =>
                            setFieldValue(`otp[${i}]`, digit)
                          );
                          // focus the last filled box
                          const lastIndex = Math.min(
                            index + pasted.length - 1,
                            5
                          );
                          inputRefs.current[lastIndex]?.focus();
                        }}
                        inputProps={{
                          maxLength: 1,
                          style: {
                            textAlign: "center",
                            fontSize: Fonts.GeneralHeaderFont.fontSize,
                            fontWeight: Fonts.GeneralHeaderFont.fontWeight,
                            width: "45px",
                          },
                        }}
                        variant="outlined"
                        error={
                          touched.otp &&
                          Boolean(errors.otp) &&
                          values.otp[index] === ""
                        }
                      />
                    )}
                  </Field>
                ))}
              </Box>
              {((touched.otp && errors.otp) || errorMessage) && (
                <Typography
                  color="error"
                  variant="caption"
                  align="center"
                  display="block"
                  sx={{ fontSize: Fonts.GeneralLargeBodyFont.fontSize }}
                >
                  {errorMessage ? errorMessage : "OTP Required"}
                </Typography>
              )}

              <DialogActions sx={{ flexDirection: "column", mt: 2, gap: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    textTransform: "none",
                    fontWeight: Fonts.GeneralSubHeaderFont.fontWeight,
                    backgroundColor: "#1675e0",
                    width: "100%",
                    fontSize: Fonts.GeneralSubHeaderFont.fontSize,
                  }}
                  disabled={isSubmitting}
                >
                  Verify
                </Button>

                <Button
                  variant="text"
                  disabled={coolDown > 0 || loading || isSubmitting}
                  onClick={handleResend}
                  sx={{ textTransform: "none", fontSize: Fonts.GeneralButtonFont.fontSize }}
                >
                  {coolDown > 0 ? `Resend code in ${coolDown}s` : "Resend Code"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Box>
    </Dialog>
  );
};

export default OtpModal;
